import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ConfidenceBar, CoverageBars } from "../components/charts";
import { StatTiles } from "../components/StatTiles";
import { DownloadIcon } from "../components/DownloadIcon";
import { DocxPreview } from "../components/DocxPreview";
import { EventLog, describe } from "../components/EventLog";
import { HtmlPreview } from "../components/HtmlPreview";
import { FindingsTable } from "../components/FindingsTable";
import { Markdown } from "../components/Markdown";
import { PhaseTimeline } from "../components/PhaseTimeline";
import { PROJECT_STATUS_TONE, ScoreControl } from "../components/ScoreControl";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  InlineNote,
  Notice,
  Spinner,
  StatusBadge,
  runTone,
} from "../components/primitives";
import { api } from "../lib/api";
import { bytes, count, duration, humanise, money, relativeTime } from "../lib/format";
import { DiscussRun } from "../components/DiscussRun";
import { Mark } from "../components/Mark";
import { SpendConfirm } from "../components/SpendConfirm";
import {
  BOARD_SUMMARY,
  builtDeck,
  builtSummary,
  group,
  leafOf,
  primaryFile,
  stemOf,
} from "../lib/artefacts";
import { modelLabel, playbookTitle, projectTypeLabel } from "../lib/labels";
import { taskLog } from "../lib/tasklog";
import type { Group } from "../lib/artefacts";
import type { Artefact, DeckSlide, RunEvent, RunTask } from "../lib/types";
import { useEventStream } from "../lib/useEventStream";

/** How often a live run is re-read when the stream is not carrying the load. */
const LIVE_POLL_MS = 2_500;

const TABS = [
  "overview",
  "evidence",
  "deliverables",
  "sources",
  "activity",
  // Last, because it is what you do *after* reading the deliverable —
  // and its own tab rather than a control inside Deliverables, because
  // the conversation is about the whole run, not the selected file.
  // It carries `?tab=discuss`, so a discussion can be linked to.
  "discuss",
] as const;
type Tab = (typeof TABS)[number];

export function RunDetail() {
  const { runId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [resumeCost, setResumeCost] = useState("");

  const tabParam = params.get("tab");
  //     `show` carries a filter into the tab it opens, so a count above the
  //     tabs can land on what it counted rather than on the whole list.
  const go = (next: Tab, show?: string) =>
    setParams(show ? { tab: next, show } : { tab: next }, { replace: true });
  const setTab = (next: Tab) => go(next);

  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.run(runId),
    // Belt and braces. The event stream drives the page when it works, but a
    // proxy, an extension or a sleeping tab can drop it without anything here
    // noticing — and a dashboard that has quietly stopped updating looks
    // exactly like a run that has quietly stopped. Polling while live costs
    // one small JSON read every few seconds and removes that whole class of
    // "is it broken or is it thinking?".
    refetchInterval: (query) =>
      query.state.data?.status === "running" ||
      // A finished run with work still going on it — a deck being built from
      // a deliverable. The page has to keep asking or the elapsed time it is
      // showing stops climbing while the work carries on.
      query.state.data?.task?.status === "running"
        ? LIVE_POLL_MS
        : false,
    staleTime: 0,
  });
  // Queued counts as live: it is waiting for a slot, the page has to keep
  // polling to notice when it starts, and Cancel has to stay offered —
  // giving up a place in the queue is the main reason to press it.
  const isLive =
    run.data?.status === "running" ||
    run.data?.status === "queued" ||
    run.data?.live === true;

  //     Post-hoc work: a deck being built from a deliverable somebody read.
  //     Kept separate from `isLive` on purpose — this run is not an engagement
  //     in flight, Cancel does not apply to it, and the queue must not count
  //     it. What it shares with a live run is only that somebody is waiting.
  const task = run.data?.task ?? null;
  const working = task?.status === "running";
  const queued = run.data?.queue_position ?? null;

  //     A finished run opens on what it produced. It opened on Overview, whose
  //     first card is the phase table — turns, minutes and dollars per phase —
  //     which is how the run went, not what it found. A live run still opens
  //     there: while it runs, how it is going is the question.
  const tab: Tab = TABS.includes(tabParam as Tab)
    ? (tabParam as Tab)
    : run.data?.status === "completed"
      ? "deliverables"
      : "overview";

  //     The poll above stops the moment the task stops, which can be the same
  //     tick the deck lands in — so the last state it saw is the one without
  //     it. Keyed on `finished_at` so this fires once per finished build and
  //     not on every refetch that carries the same completed task.
  const taskFinishedAt = task && task.status !== "running" ? task.finished_at : null;
  useEffect(() => {
    if (taskFinishedAt === null) return;
    queryClient.invalidateQueries({ queryKey: ["artefacts", runId] });
  }, [taskFinishedAt, queryClient, runId]);

  const { events, state } = useEventStream(runId, true);

  const findings = useQuery({
    queryKey: ["findings", runId],
    queryFn: () => api.findings(runId),
    refetchInterval: isLive ? LIVE_POLL_MS : false,
    staleTime: 0,
  });
  const sources = useQuery({
    queryKey: ["sources", runId],
    queryFn: () => api.sources(runId),
    enabled: tab === "sources" || tab === "overview",
    refetchInterval: isLive ? LIVE_POLL_MS : false,
    staleTime: 0,
  });
  const artefacts = useQuery({
    queryKey: ["artefacts", runId],
    queryFn: () => api.artefacts(runId),
    //     `working` as well as `isLive`, because a deck is built *after* the
    //     run finished: on `isLive` alone the listing stopped polling exactly
    //     while the one new file anybody was waiting for was being written,
    //     and the deck appeared on the next full page load.
    refetchInterval: isLive || working ? LIVE_POLL_MS : false,
  });
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });

  const cancel = useMutation({
    mutationFn: () => api.cancel(runId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["run", runId] }),
  });

  // A stalled run has already paid for the phases that finished. Resuming
  // keeps them — starting again buys the same evidence a second time.
  const resume = useMutation({
    mutationFn: () =>
      api.resume(runId, resumeCost.trim() === "" ? null : Number(resumeCost)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["run", runId] });
      queryClient.invalidateQueries({ queryKey: ["runs"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  if (run.isLoading) return <Spinner label="Loading run" />;
  if (run.isError) return <ErrorNote error={run.error} />;
  const manifest = run.data!;
  // Which phase stopped it. The manifest names the reason but not where it
  // happened, and "in phase scope" is the difference between a configuration
  // problem and one that appeared halfway through the research.
  const failedPhase = manifest.phases?.find((p) => p.status === "failed")?.key;
  const evidence = manifest.evidence ?? {
    findings: 0,
    coverage: {},
    confidence_mix: { high: 0, medium: 0, low: 0 },
    sources_observed: 0,
    unsourced_findings: [],
    unverified_findings: [],
  };
  const playbook = playbooks.data?.find(
    (p) => p.key === manifest.engagement?.playbook,
  );

  const unsourced = evidence.unsourced_findings?.length ?? 0;
  const unverified = evidence.unverified_findings?.length ?? 0;
  const problems = unsourced + unverified;
  //     Says what it counted. "76 unsourced or unverified" sat above a panel
  //     reading "Unsourced findings: none" — the label claimed a kind of
  //     problem the run did not have.
  const problemLabel =
    unsourced && unverified ? "unsourced or unverified" : unsourced ? "unsourced" : "unverified";
  const phasesDone = manifest.phases.filter((p) => p.status === "done").length;
  const phasesPlanned = playbook
    ? playbook.phases.filter((p) => !p.on_demand).length
    : manifest.phases.length;
  const runningPhase = manifest.phases.find((p) => p.status === "running")?.key ?? null;

  //     While post-hoc work is going the clock is that work's, not the
  //     engagement's: a run that finished on Tuesday would otherwise show
  //     three days "elapsed" for a deck that has been building for a minute.
  const elapsed = working
    ? (task?.elapsed_s ?? 0)
    : manifest.finished_at
      ? (new Date(manifest.finished_at).getTime() -
          new Date(manifest.started_at).getTime()) /
        1000
      : (Date.now() - new Date(manifest.started_at).getTime()) / 1000;

  return (
    <div className="space-y-4">
      {/* ---------------------------------------------------------- header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/engagements" className="text-xs text-muted hover:text-ink">
            ← All engagements
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-lg font-semibold">
            {manifest.engagement?.target ?? manifest.run_id}
            <StatusBadge tone={runTone(manifest.status)}>{manifest.status}</StatusBadge>
            {queued !== null && (
              <span className="font-mono text-xs text-muted">
                waiting for a slot — position {queued}
              </span>
            )}
          </h1>
          <p className="mt-0.5 text-xs text-muted">
            {[
              playbookTitle(playbooks.data, manifest.engagement?.playbook),
              manifest.engagement?.sector,
              manifest.engagement?.geography,
              manifest.engagement?.depth && `${manifest.engagement.depth} depth`,
              modelLabel(
                options.data,
                manifest.engagement?.provider,
                manifest.engagement?.model,
              ),
            ]
              .filter(Boolean)
              .join(" · ")}
            {" · started "}
            {relativeTime(manifest.started_at)}
          </p>

        </div>
        {/*
          The run id, and the actions under it. It is the top-right corner
          because it is an identifier rather than a heading: the target company
          is what this page is *about*, the id is what you quote when you leave
          it — `cdd republish <id>`, `cdd resume <id>`, a support message. That
          is also why it copies on click.
        */}
        <div className="flex min-w-0 flex-col items-end gap-2">
          <RunIdChip runId={manifest.run_id} />
          {isLive && (
            <Button
              variant="danger"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? "Cancelling…" : "Cancel run"}
            </Button>
          )}
          {!isLive && (manifest.status === "failed" || manifest.status === "cancelled") && (
            <div className="flex items-end gap-2">
              {/*
                The ceiling belongs to the *run*, and the commonest reason to
                resume is that the run stopped *because* of it — so raising it
                has to be reachable from here. Without this field the button
                re-ran against the same ceiling it had just exhausted, and the
                only way to change it was the CLI.
              */}
              <div>
                <label className="field-label" htmlFor="resume-cost">
                  New ceiling (USD)
                </label>
                <input
                  id="resume-cost"
                  type="number"
                  min={1}
                  step="any"
                  className="field tabular w-32"
                  value={resumeCost}
                  placeholder={(manifest.usage?.cost_usd ?? 0).toFixed(0)}
                  onChange={(e) => setResumeCost(e.target.value)}
                />
              </div>
              <Button
                variant="primary"
                onClick={() => resume.mutate()}
                disabled={resume.isPending}
                title="Continue from the first phase that did not finish. Findings, sources and spend carry forward — so leaving the ceiling as it was will stop again immediately."
              >
                {resume.isPending ? "Resuming…" : "Resume run"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/*
        What the system counted, in the same boxes the Engagements page opens
        with (#132). It was one line of facts here (#124) and boxes there,
        and the line read as small print on both — the overview went back to
        boxes first. Each opens the tab it is counted from, and the open tab's
        box is outlined, as the overview outlines the filter it is showing.
      */}
      <StatTiles
        items={[
          {
            value: count(evidence.findings),
            label: "findings",
            onClick: () => go("evidence"),
            active: tab === "evidence",
          },
          {
            value: count(evidence.sources_observed),
            label: "sources retrieved",
            onClick: () => go("sources"),
            active: tab === "sources",
          },
          {
            value: count(problems),
            label: problemLabel,
            tone: problems > 0 ? "warning" : undefined,
            onClick: problems > 0 ? () => go("evidence", "attention") : undefined,
            title: "Findings with no source, or citing a page that was never retrieved",
          },
          {
            value: `${phasesDone}/${phasesPlanned}`,
            label: runningPhase ? `phases · ${humanise(runningPhase)}` : "phases",
            onClick: () => go("overview"),
            active: tab === "overview",
          },
          {
            value: duration(elapsed),
            label: working ? `building the ${task?.kind}` : isLive ? "in flight" : "elapsed",
          },
          {
            value: money(manifest.usage?.cost_usd),
            label: "spent",
            title: `${count(manifest.usage?.output_tokens)} output tokens`,
          },
        ]}
      />

      {resume.isError && <ErrorNote error={resume.error} />}

      {manifest.job_error && <ErrorNote error={manifest.job_error} />}

      {/*
        Why it ended this way, at the top of the page it ended on.
        `job_error` above covers a crash *outside* the orchestrator; this is
        the run's own account, and it used to exist only in `events.jsonl` —
        so a run refused on its first API call read as the word "failed" and
        nothing else.
      */}
      {manifest.error && (
        <Notice
          tone={manifest.status === "failed" ? "critical" : "neutral"}
          title={`Why it ${manifest.status}`}
        >
          <p className="whitespace-pre-wrap break-words">{manifest.error}</p>
          {failedPhase && <p className="label">in phase {failedPhase}</p>}
        </Notice>
      )}

      <RunNotes
        notes={[
          ...(manifest.resumed
            ? ["This run was resumed; earlier phases ran in a previous session."]
            : []),
          ...(manifest.notes ?? []),
        ]}
        operator={manifest.operator_notes ?? []}
        open={manifest.status !== "completed"}
      />

      {/* ------------------------------------------------------------ tabs */}
      {/*
        The WAI-ARIA tabs pattern: a tablist, one tab in the tab order, arrows
        between them. They were buttons with `aria-current`, so a screen
        reader announced six buttons and nothing about which section was open
        (#124). The selection is still the `?tab=` in the URL.
      */}
      <div
        role="tablist"
        aria-label="Run sections"
        className="flex gap-1 overflow-x-auto border-b border-line"
        onKeyDown={(event) => {
          const at = TABS.indexOf(tab);
          const to =
            event.key === "ArrowRight"
              ? TABS[(at + 1) % TABS.length]
              : event.key === "ArrowLeft"
                ? TABS[(at - 1 + TABS.length) % TABS.length]
                : event.key === "Home"
                  ? TABS[0]
                  : event.key === "End"
                    ? TABS[TABS.length - 1]
                    : null;
          if (!to) return;
          event.preventDefault();
          setTab(to);
          document.getElementById(`tab-${to}`)?.focus();
        }}
      >
        {TABS.map((name) => (
          <button
            key={name}
            id={`tab-${name}`}
            role="tab"
            type="button"
            aria-selected={tab === name}
            aria-controls="run-tabpanel"
            tabIndex={tab === name ? 0 : -1}
            onClick={() => setTab(name)}
            className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm capitalize transition-colors ${
              tab === name
                ? "border-accent font-medium text-ink"
                : "border-transparent text-ink-2 hover:text-ink"
            }`}
          >
            {name}
            {name === "evidence" && findings.data?.length ? (
              <span className="ml-1.5 font-mono text-xs tabular-nums text-muted">
                {count(findings.data.length)}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="run-tabpanel"
        aria-labelledby={`tab-${tab}`}
        className="space-y-4"
      >

      {tab === "overview" && (
        <div className="grid gap-4 lg:grid-cols-[1fr_26rem]">
          <div className="space-y-4">
            <Card title="Phases">
              <PhaseTimeline
                phases={manifest.phases}
                playbook={playbook}
                plannedPhases={manifest.planned_phases}
              />
            </Card>
            <Card title="Coverage by workstream">
              <CoverageBars coverage={evidence.coverage ?? {}} />
            </Card>
            {manifest.engagement?.thesis && (
              <Card title="Thesis under test">
                <p className="px-4 py-3 text-sm text-ink-2">
                  {manifest.engagement.thesis}
                </p>
              </Card>
            )}
          </div>
          <div className="space-y-4">
            {manifest.project && (
              <Card title="Project">
                <dl className="divide-y divide-line text-sm">
                  <Row label="Name" value={manifest.project.name} />
                  <Row label="Client" value={manifest.project.client ?? "internal"} />
                  <Row
                    label="Type"
                    value={projectTypeLabel(playbooks.data, manifest.project.project_type)}
                  />
                </dl>
                <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
                  <StatusBadge tone={PROJECT_STATUS_TONE[manifest.project.status]}>
                    {manifest.project.status}
                  </StatusBadge>
                  <ScoreControl project={manifest.project} compact />
                </div>
                <p className="px-4 pb-3 text-xs text-muted">
                  {/* It said "You have just read the report" to everybody,
                      including somebody who had not opened it (#124). */}
                  Once you have read the report, grade it here.{" "}
                  <Link to="/projects" className="text-accent underline">
                    All projects
                  </Link>
                </p>
              </Card>
            )}
            <Card title="Confidence mix">
              <ConfidenceBar mix={evidence.confidence_mix ?? {}} />
            </Card>
            <Card title="Provenance">
              <dl className="divide-y divide-line text-sm">
                <Row label="Sources retrieved" value={String(evidence.sources_observed ?? 0)} />
                <Row
                  label="Unsourced findings"
                  value={(evidence.unsourced_findings ?? []).map((i) => `F${i}`).join(", ") || "none"}
                  warn={(evidence.unsourced_findings ?? []).length > 0}
                />
                <Row
                  label="Unverified citations"
                  value={(evidence.unverified_findings ?? []).map((i) => `F${i}`).join(", ") || "none"}
                  warn={(evidence.unverified_findings ?? []).length > 0}
                />
              </dl>
            </Card>
            {/* Provenance for whoever is debugging the system, kept rather
                than dropped: `manifest.json` records these per phase so a
                finding can be traced to the wording that produced it, and a
                report written under an older prompt is not directly
                comparable with a newer one. It is simply not what the reader
                came for, so it sits last. */}
            <Card title="Prompt versions">
              <dl className="divide-y divide-line text-sm">
                {Object.entries(manifest.prompt_versions ?? {}).map(([name, hash]) => (
                  <Row key={name} label={name} value={hash} mono />
                ))}
              </dl>
            </Card>
          </div>
        </div>
      )}

      {tab === "evidence" && (
        <Card>
          {findings.isLoading ? (
            <Spinner />
          ) : (
            <FindingsTable
              findings={findings.data ?? []}
              problemsOnly={params.get("show") === "attention"}
            />
          )}
        </Card>
      )}

      {tab === "deliverables" && (
        <Deliverables
          runId={runId}
          artefacts={artefacts.data ?? []}
          declared={run.data?.declared_deliverables ?? []}
          task={task}
          // The same stream the Activity tab reads. A deck build writes its
          // own tool calls into `events.jsonl`, so the frame that shows the
          // build has a source for what it is doing and needs no second
          // channel.
          events={events}
          onCitationClick={(id) => {
            setTab("evidence");
            window.setTimeout(
              () => document.getElementById(`finding-${id}`)?.scrollIntoView(),
              50,
            );
          }}
        />
      )}

      {tab === "discuss" && (
        <Card>
          <DiscussRun runId={runId} />
        </Card>
      )}

      {tab === "sources" && (
        <Card title={`Retrieved sources (${sources.data?.length ?? 0})`}>
          {sources.isLoading ? (
            <Spinner />
          ) : (sources.data ?? []).length === 0 ? (
            <Empty
              title="Nothing retrieved yet"
              hint="Pages the agent actually fetches are logged here; findings cite against this list."
            />
          ) : (
            <ul className="divide-y divide-line">
              {(sources.data ?? []).map((source) => (
                <li key={source.canonical} className="px-4 py-2.5">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-accent underline underline-offset-2"
                  >
                    {source.title ?? source.url}
                  </a>
                  <p className="mt-0.5 text-xs text-muted">
                    via {source.via} · {relativeTime(source.observed_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === "activity" && (
        <Card className="h-[36rem] overflow-hidden">
          <EventLog events={events} state={state} />
        </Card>
      )}
      </div>
    </div>
  );
}

/**
 * What the run wants a person to know, folded under one line.
 *
 * The notes used to be a card above everything, so the first thing a deal
 * team read on a finished CDD was a sentence about a skill module's `SKILLS`
 * list — the system's packaging, not their deliverable (#124). The reader's
 * notes are a disclosure now, open when the run did not complete (that is
 * when they explain the page), and the operator's notes are a second one that
 * only an admin is ever sent.
 */
function RunNotes({
  notes,
  operator,
  open,
}: {
  notes: string[];
  operator: string[];
  open: boolean;
}) {
  if (notes.length === 0 && operator.length === 0) return null;
  return (
    <div className="space-y-1 text-xs">
      {notes.length > 0 && (
        <details open={open} className="group">
          <summary className="cursor-pointer select-none text-ink-2 hover:text-ink">
            {notes.length === 1 ? "1 note" : `${notes.length} notes`} about how
            this run went
          </summary>
          <ul className="mt-1.5 space-y-1 border-l border-line pl-3 leading-relaxed text-ink-2">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </details>
      )}
      {operator.length > 0 && (
        <details>
          <summary className="cursor-pointer select-none text-muted hover:text-ink">
            For the operator · {operator.length}
          </summary>
          <ul className="mt-1.5 space-y-1 border-l border-line pl-3 font-mono text-xs leading-relaxed text-muted">
            {operator.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  warn,
  mono,
}: {
  label: string;
  value: string;
  warn?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-4 py-2">
      <dt className="text-ink-2">{label}</dt>
      <dd className={`text-right ${mono ? "font-mono text-xs text-muted" : ""}`}>
        {/* A dot beside the ids, in ink: as text, serious read 2.64:1 and
            looked like the one orange reserved for the finding. */}
        {warn ? (
          <span className="inline-flex items-baseline gap-1.5 text-ink">
            <span aria-hidden className="size-1.5 shrink-0 -translate-y-px rounded-full bg-serious" />
            <span className="font-mono text-xs">{value}</span>
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/**
 * The same deliverable in three renderings. A run publishes `<stem>.md`,
 * `<stem>.docx` and `<stem>.html`, so picking one file offers all three and
 * shows the ones this run did not produce as disabled rather than hiding them —
 * an absent format should be legible, not invisible.
 */
const FORMATS = [
  { key: "md", label: "Markdown" },
  { key: "docx", label: "DocX" },
  { key: "html", label: "HTML" },
] as const;

type Format = (typeof FORMATS)[number]["key"];

function formatOf(name: string): Format | null {
  const match = /\.(md|docx|html)$/i.exec(name);
  return match ? (match[1]!.toLowerCase() as Format) : null;
}

/**
 * How a file has to be fetched, which is not the same question as which of the
 * three deliverable formats it is.
 *
 * `/artefacts/{name}` returns JSON with a `content` string and **404s on
 * anything that is not text** — `read_artefact` returns None rather than
 * mojibake for a binary. Everything below therefore has to go through
 * `/files/{name}`, which serves bytes with a real media type.
 *
 * Getting this wrong produced `Error — no artefact
 * 'images/…-d828dcfe.jpg'`: an image fell through to the text route because it
 * was not one of md/docx/html, and the 404 was reported as a missing file
 * rather than the wrong request.
 */
type Kind = "text" | "image" | "docx" | "html" | "deck" | "sheet" | "binary";

/**
 * Decided from the server's `media_type`, which the artefact listing carries
 * for exactly this purpose ("so the client picks a viewer rather than guessing
 * from the suffix"). A `text/*` prefix covers every text file the workspace can
 * hold without this having to keep a list of extensions in step.
 */
function kindOf(mediaType: string): Kind {
  const type = mediaType.split(";")[0]!.trim().toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type === "text/html") return "html";
  if (type.endsWith("wordprocessingml.document")) return "docx";
  if (type.endsWith("presentationml.presentation")) return "deck";
  if (type.endsWith("spreadsheetml.sheet")) return "sheet";
  if (
    type.startsWith("text/") ||
    type === "application/json" ||
    type === "application/x-ndjson"
  )
    return "text";
  return "binary";
}

/**
 * An image the agent fetched and embedded. Served from `/files/`, which sets a
 * real media type — the JSON route cannot carry it at all.
 */
/**
 * The run id, top right, copied on click.
 *
 * Mono because it is machine-written — the same rule the deliverables follow,
 * where Archivo carries what a person wrote and IBM Plex Mono carries run
 * numbers, source ids and timestamps.
 *
 * It copies because there is only one reason to read a run id: to use it
 * somewhere else. Selecting forty characters of hyphenated slug out of a
 * heading is the thing this replaces. `navigator.clipboard` needs a secure
 * context, which localhost and https both are and a plain-http host is not —
 * so a failure says so rather than looking like nothing happened.
 */
function RunIdChip({ runId }: { runId: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 1600);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      title={`${runId}\n\nClick to copy`}
      aria-label={`Run id ${runId}. Copy to clipboard.`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(runId);
          setState("copied");
        } catch {
          setState("failed");
        }
      }}
      className="max-w-full truncate rounded-sm border border-line bg-surface-2 px-2 py-1 font-mono text-xs leading-none text-muted transition-colors hover:border-line-strong hover:text-ink"
    >
      {state === "copied" ? "copied" : state === "failed" ? "copy failed" : runId}
    </button>
  );
}

function ImagePreview({ runId, name }: { runId: string; name: string }) {
  return (
    <div className="space-y-3 px-5 py-5">
      <img
        src={api.fileUrl(runId, name)}
        alt={name}
        className="max-h-[70vh] max-w-full rounded-lg border border-line object-contain"
      />
      <p className="text-xs text-muted">
        {name} ·{" "}
        <a className="underline" href={api.fileUrl(runId, name, true)}>
          download
        </a>
        {" · "}
        an image is a source: it was fetched through `fetch_image` and recorded
        in the source registry, so a finding citing it verifies like any page.
      </p>
    </div>
  );
}

/**
 * A deck as its outline.
 *
 * There is no way to render PowerPoint in the browser without shipping a
 * rendering engine, and the deliverable CSP forbids fetching one. But
 * `skills/deck` refuses a slide title of fewer than four words because *titles
 * read in sequence are the argument* — so the outline is the part worth
 * reading, and the file is one click away.
 */
function DeckPreview({
  name,
  href,
  slides,
  loading,
  error,
}: {
  name: string;
  href: string;
  slides?: DeckSlide[];
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  // Said once, as a count, and in the one colour that means "look here". It
  // used to be an orange "no title" on every row — thirteen findings on one
  // screen, which is the same as none (#124).
  const untitled = (slides ?? []).filter((slide) => !slide.title).length;

  return (
    <div className="space-y-4 px-5 py-5">
      <p className="text-xs text-muted">
        {name} · {slides?.length ?? 0} slides ·{" "}
        <a className="underline" href={href}>
          download
        </a>
        {" · "}read the titles in order: they should carry the argument on their
        own.
      </p>
      {untitled > 0 && (
        <InlineNote tone="warning">
          {untitled === slides?.length
            ? "No slide has a title the outline can read"
            : `${untitled} of ${slides?.length} slides ${untitled === 1 ? "has" : "have"} no title the outline can read`}
          , so the argument cannot be followed from the titles alone.
        </InlineNote>
      )}
      <ol className="space-y-3">
        {slides?.map((slide) => (
          <li key={slide.number} className="border-l-2 border-line pl-4">
            <div className="flex gap-2 text-sm">
              <span className="tabular shrink-0 text-muted">{slide.number}</span>
              <span className="font-medium text-ink">
                {slide.title || <span className="font-normal italic text-muted">untitled</span>}
              </span>
            </div>
            {slide.body.length > 0 && (
              <ul className="mt-1 space-y-0.5 pl-6 text-xs text-ink-2">
                {slide.body.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The frame while a deck is being built (#102), or the board summary written
 * (#129).
 *
 * It replaces the preview rather than sitting beside it, because the reader
 * pressed a button and the answer to "did that do anything" has to be where
 * they are looking. The summary was a pop-up over the page until #129; it
 * works here now, the same way, so the two buttons that cost a model call
 * answer in one place and one form. The mark loops for the length of the wait
 * — the second rule for that is *somebody is waiting*, which is the whole of
 * this state, and it is mounted with the work and unmounted with it, so it
 * never becomes a logo that will not settle.
 *
 * **The seconds are real and nothing else would be.** Nothing counts the steps
 * of either job, so a percentage would be invented. The elapsed time is read
 * off the task the server is keeping, which is also what makes this survive a
 * reload: the work outlives the request that started it.
 */
function TaskWorking({
  kind,
  source,
  elapsed,
  log,
}: {
  kind: "deck" | "summary";
  source: string | null;
  elapsed: number;
  /** What the agent has done so far in this task, oldest first. */
  log: RunEvent[];
}) {
  const scroller = useRef<HTMLDivElement>(null);

  // Follow the tail. A log that has to be scrolled by somebody watching a
  // spinner is a log nobody reads.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length]);

  const copy =
    kind === "summary"
      ? {
          first: "Starting — the first step is reading the run's documents.",
          doing: "Writing the board summary",
          after:
            "Eight messages a board needs, each on the run's own numbers and citations — the summary is shown here when it lands, and listed with the deliverables.",
        }
      : {
          first: "Starting the build — the first step is reading the deck method.",
          doing: `Building a deck${source ? ` from ${leafOf(source)}` : ""}`,
          after:
            "The storyline is written and checked before any slide exists, then the slides are laid out and measured — the deck is shown here when it lands.",
        };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-5 px-5 py-12"
    >
      <Mark size={96} animate loop />

      {/*
        **The log replaced the progress bar** (#107), and it is a better
        answer to the same question. The bar was honestly indeterminate —
        nothing counts the steps — so what it said was "still going", which is
        also what the spinner says. These rows say *what* is going.
      */}
      <div className="w-full max-w-lg">
        <div
          ref={scroller}
          className="h-28 overflow-y-auto rounded-sm border border-line bg-surface-2 px-3 py-2 font-mono text-xs leading-relaxed"
        >
          {log.length === 0 ? (
            <p className="text-muted">{copy.first}</p>
          ) : (
            log.map((event, index) => {
              const { text, tone } = describe(event);
              return (
                <div key={index} className={`truncate ${tone}`} title={text}>
                  {text}
                </div>
              );
            })
          )}
        </div>
        <p className="mt-2 text-center text-xs text-muted">
          {copy.doing} · <span className="tabular">{Math.round(elapsed)}s</span>
        </p>
      </div>

      <p className="max-w-sm text-center text-xs text-muted">{copy.after}</p>
    </div>
  );
}

/** Anything with no viewer — a workbook, an archive. Say so and offer it. */
function Download({ name, href }: { name: string; href: string }) {
  return (
    <div className="px-5 py-8 text-center">
      <p className="text-sm text-ink">{name}</p>
      <p className="mt-1 text-xs text-muted">
        No preview for this format — it opens in the application that made it.
      </p>
      <a
        href={href}
        className="mt-4 inline-block rounded-lg border border-line px-4 py-2 text-sm text-ink hover:bg-surface-2"
      >
        Download
      </a>
    </div>
  );
}

/**
 * The download beside one row in the file list (#96).
 *
 * `?download=1` rather than the `download` attribute alone: the preview and
 * this share one route, and the header is what makes the browser save a
 * `.md` or an `.html` instead of rendering it in place. The attribute stays
 * for the filename it suggests.
 *
 * It is always visible rather than appearing on hover — a hover-only control
 * does not exist on a touch screen, and a file list is exactly where somebody
 * arrives wanting the file. Muted until hovered or focused, so twenty rows do
 * not read as twenty buttons.
 */
function DownloadLink({ runId, name }: { runId: string; name: string }) {
  return (
    <a
      href={api.fileUrl(runId, name, true)}
      // No name of its own: the server names the file after the run (#137),
      // and a value here would compete with that.
      download
      title={`Download ${leafOf(name)}`}
      aria-label={`Download ${leafOf(name)}`}
      // The row's own click selects the file; this one must not also do that.
      onClick={(event) => event.stopPropagation()}
      className="mr-2 shrink-0 rounded-sm p-1.5 text-muted hover:bg-surface hover:text-ink focus-visible:text-ink"
    >
      <DownloadIcon />
    </a>
  );
}

/**
 * One heading and the files under it, with an arrow when there is something to
 * hide.
 *
 * Deliverables and the evidence arrive open and everything else closed, which
 * is the whole point of the split: a specialist's working papers and ten
 * product photographs used to sit in one flat list with the document the
 * reader came for, and the document is the shortest part of it.
 */
function FileGroup({
  runId,
  group: section,
  active,
  onSelect,
}: {
  runId: string;
  group: Group;
  active: string | null;
  onSelect: (name: string) => void;
}) {
  const [open, setOpen] = useState(section.open);

  // A file selected inside a collapsed group would leave the preview showing
  // something the list does not admit to holding.
  const holdsActive = section.files.some((f) => f.name === active);
  const shown = open || holdsActive;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-baseline gap-2 px-4 py-2 text-left hover:bg-surface-2"
        aria-expanded={shown}
      >
        <span
          className={`shrink-0 text-xs text-muted transition-transform ${
            shown ? "rotate-90" : ""
          }`}
          aria-hidden="true"
        >
          ▶
        </span>
        <span className="truncate text-xs font-semibold text-ink-2">{section.title}</span>
        <span className="ml-auto shrink-0 font-mono text-xs tabular-nums text-muted">
          {section.files.length}
        </span>
      </button>
      {section.hint && shown && (
        <p className="px-4 pb-1 pl-8 text-xs text-muted">{section.hint}</p>
      )}
      {shown && (
        <ul>
          {section.files.map((file) => (
            <li
              key={file.name}
              className={`flex items-center hover:bg-surface-2 ${
                active === file.name ? "bg-surface-2" : ""
              }`}
            >
              {/* The row and the download are two actions, so they are two
                  controls: an anchor inside a button is not a thing a browser
                  will honour, and a row that downloaded on click would take
                  the preview away from the reader who only wanted to look. */}
              <button
                onClick={() => onSelect(file.name)}
                className={`flex min-w-0 flex-1 items-baseline justify-between gap-2 py-1.5 pl-8 pr-2 text-left text-sm ${
                  active === file.name ? "font-medium" : ""
                }`}
                title={file.name}
              >
                {/* The directory is the group's heading, so repeating it on
                    every row would push the filename out of a 16rem column. */}
                <span className="truncate">{leafOf(file.name)}</span>
                <span className="tabular shrink-0 text-xs text-muted">
                  {bytes(file.size_bytes)}
                </span>
              </button>
              <DownloadLink runId={runId} name={file.name} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Deliverables({
  runId,
  artefacts,
  declared,
  task,
  events,
  onCitationClick,
}: {
  runId: string;
  artefacts: Artefact[];
  /** From the run's manifest — the playbook's own answer to "what is the
   * product here", rather than a list of filenames held in this file. */
  declared: string[];
  /** Post-hoc work on this run, when there is some. The pptx button reads it
   * rather than keeping its own pending flag: the work outlives the request
   * that started it, so a reload has to find it on the run, not in a hook. */
  task: RunTask | null;
  /** The run's events, for the log a deck build shows while it works. */
  events: RunEvent[];
  onCitationClick: (id: number) => void;
}) {
  const groups = useMemo(() => group(artefacts, declared), [artefacts, declared]);
  const ordered = useMemo(() => groups.flatMap((g) => g.files), [groups]);

  const [selected, setSelected] = useState<string | null>(null);
  //     The summary asks first too, in the same place as the deck (#129). It
  //     was a pop-up that opened over the page.
  const [confirmingSummary, setConfirmingSummary] = useState(false);
  //     A deck costs a model session, so pressing the button asks first and
  //     says what it may spend (#124). It used to start on the click, with the
  //     only explanation in a hover title that never mentioned money.
  const [confirmingDeck, setConfirmingDeck] = useState(false);
  //     The file list is a rail the reader can fold away, so the document —
  //     the thing this tab is for — can take the width (#124). Folded from
  //     the start below `lg`, where it would otherwise stack above the
  //     preview and push the report below the fold again.
  const [rail, setRail] = useState(
    () => typeof window === "undefined" || window.matchMedia("(min-width: 1024px)").matches,
  );
  const primary = useMemo(() => primaryFile(artefacts, declared), [artefacts, declared]);
  const active = selected ?? primary;

  const queryClient = useQueryClient();
  const deck = useMutation({
    mutationFn: (name: string) => api.buildDeck(runId, name),
    // The run is what carries the task, and the page polls it — so asking the
    // run again is what turns the button into a visible elapsed time.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["run", runId] }),
  });

  const summary = useMutation({
    mutationFn: (force: boolean) => api.writeSummary(runId, force),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["run", runId] }),
  });

  //     A deck is built from the argument, and the argument is in the `.md`.
  const isMarkdown = Boolean(active && active.toLowerCase().endsWith(".md"));
  const busy = task?.kind === "deck" && task.status === "running";
  const summaryBusy = task?.kind === "summary" && task.status === "running";
  //     From the click to the first run refetch that carries the task there is
  //     a gap the server has not answered yet, and a frame that only watched
  //     `busy` would sit on the old document for it — which is exactly the
  //     second somebody is looking for a reaction.
  const building = deck.isPending || busy;
  const summarising = summary.isPending || summaryBusy;
  //     One post-hoc task per run at a time, on the server; the buttons say so.
  const working = building || summarising;
  //     A finetune runs in the panel beside the document, not in this frame,
  //     so it disables the two buttons without taking the frame away.
  const finetuning = task?.kind === "finetune" && task.status === "running";
  const hasSummary = artefacts.some((a) => a.name === BOARD_SUMMARY);
  const viewingSummary = active === BOARD_SUMMARY;

  useEffect(() => {
    if (selected && !ordered.some((a) => a.name === selected)) setSelected(null);
  }, [ordered, selected]);

  //     Show the deck when it lands (#102).
  //
  //     Watched as a *transition* rather than as a value: a completed deck
  //     task stays on the run for ever, so a reader opening this tab a week
  //     later would have their selection taken away by a build they have
  //     already seen. `waiting` is only ever set by a build this page watched
  //     start.
  const waiting = useRef(false);
  const landed = useMemo(
    () => builtDeck(task, artefacts) ?? builtSummary(task, artefacts),
    [task, artefacts],
  );
  useEffect(() => {
    if (working) {
      waiting.current = true;
      return;
    }
    // Not yet in the listing — the poll will bring it, and this runs again.
    if (!waiting.current || !landed) return;
    waiting.current = false;
    setSelected(landed);
  }, [working, landed]);

  // Which of the three formats this run actually produced for the selected
  // stem. Files with no sibling formats (images, phase transcripts) fall
  // through to the single view below.
  const siblings = useMemo(() => {
    if (!active || !formatOf(active)) return null;
    const stem = stemOf(active);
    const found = {} as Record<Format, string | undefined>;
    for (const file of artefacts) {
      const format = formatOf(file.name);
      if (format && stemOf(file.name) === stem) found[format] = file.name;
    }
    return found;
  }, [active, artefacts]);

  const activeFormat = active ? formatOf(active) : null;

  const kind = useMemo(() => {
    const record = artefacts.find((a) => a.name === active);
    return record ? kindOf(record.media_type) : null;
  }, [active, artefacts]);

  const content = useQuery({
    queryKey: ["artefact", runId, active],
    queryFn: () => api.artefact(runId, active!),
    // Only text goes through the JSON route. Testing for "not docx and not
    // html" was the bug: it let images, decks and workbooks through to an
    // endpoint that 404s on anything it cannot decode as UTF-8.
    enabled: Boolean(active) && kind === "text",
  });

  const outline = useQuery({
    queryKey: ["outline", runId, active],
    queryFn: () => api.deckOutline(runId, active!),
    enabled: Boolean(active) && kind === "deck",
  });

  if (ordered.length === 0) {
    return (
      <Card>
        <Empty
          title="No deliverables yet"
          hint="The report is written in the draft phase, after research and reconciliation."
        />
      </Card>
    );
  }

  return (
    <div className={`grid gap-4 ${rail ? "lg:grid-cols-[16rem_1fr]" : ""}`}>
      {rail && (
      <Card title="Files" className="h-fit">
        <div className="divide-y divide-line">
          {groups.map((section) => (
            <FileGroup
              key={section.key}
              runId={runId}
              group={section}
              active={active}
              onSelect={setSelected}
            />
          ))}
        </div>
      </Card>
      )}

      <Card className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-2">
            <button
              type="button"
              onClick={() => setRail((open) => !open)}
              aria-expanded={rail}
              className="rounded-sm px-2 py-1 text-xs text-ink-2 hover:bg-surface-2 hover:text-ink"
              title={rail ? "Hide the file list" : "Show every file this run wrote"}
            >
              {rail ? "Hide files" : `Files · ${ordered.length}`}
            </button>
            {/* Names the pane: the file, once the list may be folded away.
                Without it the three format buttons read as an action to take
                rather than as a choice of what is shown. */}
            <span className="min-w-0 truncate text-sm font-semibold text-ink" title={active ?? ""}>
              {active ? leafOf(stemOf(active)) : "Preview"}
            </span>
            {siblings && FORMATS.map((format) => {
              const name = siblings[format.key];
              const isActive = activeFormat === format.key;
              return (
                <button
                  key={format.key}
                  disabled={!name}
                  onClick={() => name && setSelected(name)}
                  title={
                    name ?? `This run did not produce a .${format.key} deliverable`
                  }
                  className={`rounded-sm px-3 py-1 text-sm ${
                    isActive
                      ? "bg-surface-2 font-medium text-ink"
                      : name
                        ? "text-ink-2 hover:bg-surface-2"
                        : "cursor-not-allowed text-muted opacity-50"
                  }`}
                >
                  {format.label}
                </button>
              );
            })}

          {/* The two actions, pushed right and ruled off from the format
              switchers beside them: those choose what is *shown*, these do
              something and cost a model call. They are named as actions and
              bordered like buttons — as "tl;dr" and "pptx" in the same style
              as Markdown/DocX/HTML they read as two more renderings, and a
              reader pressed one to look and paid for it (#124). */}
          <span className="flex-1" />
          <span className="flex items-center gap-2 sm:border-l sm:border-line sm:pl-3">
            {/* A summary already on disk opens for nothing; writing one, or
                writing it again from the one being read, asks first. */}
            <Button
              type="button"
              size="sm"
              onClick={() =>
                hasSummary && !viewingSummary
                  ? setSelected(BOARD_SUMMARY)
                  : setConfirmingSummary(true)
              }
              disabled={working || finetuning || confirmingSummary}
            >
              {summarising
                ? "Writing summary…"
                : viewingSummary
                  ? "Write summary again"
                  : "Board summary"}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setConfirmingDeck(true)}
              disabled={!isMarkdown || working || finetuning || confirmingDeck}
              title={
                isMarkdown
                  ? undefined
                  : "A deck is built from the .md — the .html and .docx beside it are renderings of the same text"
              }
            >
              {busy ? "Building deck…" : "Build deck"}
            </Button>
          </span>
        </div>

        {confirmingDeck && active && (
          <SpendConfirm
            action="deck"
            title={`Build a deck from ${leafOf(active)}`}
            confirmLabel="Build deck"
            onCancel={() => setConfirmingDeck(false)}
            onConfirm={() => {
              setConfirmingDeck(false);
              deck.mutate(active);
            }}
          >
            The storyline is written and checked first, then the slides are laid
            out and measured. A few minutes; you can leave this page.
          </SpendConfirm>
        )}

        {confirmingSummary && (
          <SpendConfirm
            action="summary"
            title={hasSummary ? "Write the board summary again" : "Write the board summary"}
            confirmLabel={hasSummary ? "Write it again" : "Write it"}
            onCancel={() => setConfirmingSummary(false)}
            onConfirm={() => {
              setConfirmingSummary(false);
              summary.mutate(hasSummary);
            }}
          >
            {hasSummary
              ? "The saved summary is replaced by the new one."
              : "Eight messages a board needs, read from this run\u2019s own documents and saved with the run as summary.md, so opening it again costs nothing."}
          </SpendConfirm>
        )}

        {(deck.isError || summary.isError) && (
          <div className="px-4 pt-3">
            <ErrorNote error={deck.error ?? summary.error} />
          </div>
        )}

        {/* What the last build actually did. The storyline line is the point:
            the deck method asks for the argument to be checked before any
            slide exists, and nothing enforces it — so it is said out loud
            rather than assumed. */}
        {task?.kind === "deck" && task.status !== "running" && (
          <p className="border-b border-line px-4 py-2 text-xs text-muted">
            {task.decks?.length
              ? `Built ${task.decks.join(", ")} from ${task.source}. `
              : `No deck was written from ${task.source}. `}
            {task.storyline
              ? `The argument was checked first, in ${task.storyline}.`
              : "No storyline was checked before the slides."}
            {task.error ? ` ${task.error}` : ""}
          </p>
        )}

        {/* A summary that did not land says so where it was being written. */}
        {task?.kind === "summary" && task.status !== "running" && (task.error || !task.file) && (
          <p className="border-b border-line px-4 py-2 text-xs text-muted">
            No board summary was written{task.error ? `: ${task.error}` : "."}
          </p>
        )}

        {working ? (
          <TaskWorking
            kind={summarising ? "summary" : "deck"}
            source={active}
            elapsed={task?.elapsed_s ?? 0}
            log={taskLog(events, summarising ? "summary" : "deck")}
          />
        ) : kind === "html" && active ? (
          <HtmlPreview runId={runId} name={active} events={events} />
        ) : kind === "docx" && active ? (
          <DocxPreview runId={runId} name={active} />
        ) : kind === "image" && active ? (
          <ImagePreview runId={runId} name={active} />
        ) : kind === "deck" && active ? (
          <DeckPreview
            name={active}
            href={api.fileUrl(runId, active, true)}
            slides={outline.data?.slides}
            loading={outline.isLoading}
            error={outline.error}
          />
        ) : (kind === "sheet" || kind === "binary") && active ? (
          <Download name={active} href={api.fileUrl(runId, active, true)} />
        ) : content.isLoading ? (
          <Spinner />
        ) : content.isError ? (
          <ErrorNote error={content.error} />
        ) : (
          <div className="px-5 py-5">
            <Markdown
              content={content.data?.content ?? ""}
              onCitationClick={onCitationClick}
              resolveHref={(href) => api.siblingUrl(runId, href)}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
